import { Component, OnDestroy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { G2ReviewEnableResult, G2ReviewEnableUserResult } from 'types';
import { AdminG2ReviewsService } from './../../core/services/admin-g2-reviews.service';

export interface EnableG2ReviewsDialogResult {
    changed: boolean;
}

@Component({
    selector: 'app-enable-g2-reviews-dialog',
    templateUrl: './enable-g2-reviews-dialog.component.html',
    styleUrls: ['./enable-g2-reviews-dialog.component.scss']
})
export class EnableG2ReviewsDialogComponent implements OnDestroy {
    public form = this.formBuilder.group({ userIds: '' });

    public isSubmitting = false;
    public isDone = false;
    public currentBatch = 0;
    public totalBatches = 0;

    public resultCounts: Record<G2ReviewEnableResult, number> = {
        enabled: 0,
        enabledButPromptedWithinYear: 0,
        skippedAlreadyReviewed: 0,
        notFound: 0
    };
    public flaggedResults: G2ReviewEnableUserResult[] = [];
    public failedUserIds: string[] = [];
    public failedBatchCount = 0;

    private enableSubscription: Subscription | null = null;

    public constructor(
        private matDialogRef: MatDialogRef<EnableG2ReviewsDialogComponent, EnableG2ReviewsDialogResult>,
        private formBuilder: FormBuilder,
        private adminG2ReviewsService: AdminG2ReviewsService
    ) {}

    public static openDialog(matDialog: MatDialog) {
        return matDialog.open<EnableG2ReviewsDialogComponent, void, EnableG2ReviewsDialogResult>(
            EnableG2ReviewsDialogComponent,
            {
                role: 'dialog',
                restoreFocus: true,
                disableClose: true,
                width: '500px'
            }
        );
    }

    public ngOnDestroy(): void {
        this.enableSubscription?.unsubscribe();
    }

    /** Trimmed, non-empty, unique user IDs from the textarea, in the order entered. */
    public get userIds(): string[] {
        const lines: string[] = (this.form.value.userIds ?? '').split(/\r?\n/);
        const ids = lines.map((line) => line.trim()).filter((line) => line.length > 0);
        return Array.from(new Set(ids));
    }

    public get totalEnabled(): number {
        return this.resultCounts.enabled + this.resultCounts.enabledButPromptedWithinYear;
    }

    public get changed(): boolean {
        return this.totalEnabled > 0;
    }

    public submit(): void {
        const userIds = this.userIds;
        if (!userIds.length || this.isSubmitting) return;

        this.isSubmitting = true;
        this.form.disable();

        this.enableSubscription = this.adminG2ReviewsService.enableUsersInBatches(userIds).subscribe({
            next: (outcome) => {
                this.currentBatch = outcome.batchNumber;
                this.totalBatches = outcome.totalBatches;

                if (!outcome.response) {
                    this.failedBatchCount++;
                    this.failedUserIds.push(...outcome.userIds);
                    return;
                }

                for (const item of outcome.response.results) {
                    this.resultCounts[item.result]++;
                    if (item.result !== 'enabled') this.flaggedResults.push(item);
                }
            },
            complete: () => {
                this.isSubmitting = false;
                this.isDone = true;
            }
        });
    }

    public close(): void {
        this.matDialogRef.close({ changed: this.changed });
    }
}
